import {useFormContext} from "react-hook-form";
import {z} from "zod";
import {BookingFormSchema} from "@/components/booking/form/BookingFormSchema.ts";
import {BookingDuration, BookingDurationHelper} from "@/lib/enum/BookingDuration.ts";
import {useContext, useEffect, useRef} from "react";
import {OscarContext, OscarContextType} from "@/components/context/OscarContext.tsx";
import {Core, easepick} from "@easepick/core";
import {DateTime} from "@easepick/datetime";
import {CustomRangePlugin} from "@/lib/calendar/custom_range.ts";
import {LockPlugin} from "@easepick/lock-plugin";
import coreStyle from '@easepick/core/dist/index.css?url';
import rangeStyle from '@easepick/range-plugin/dist/index.css?url';
import customDropOffStyle from '@/lib/calendar/custom_cal_dropoff.css?url';
import customRangeStyle from '@/lib/calendar/custom_range.css?url';
import lockStyle from '@easepick/lock-plugin/dist/index.css?url';

// Constants
const MinDate = new Date();
if ((MinDate.getDate() == 24 && MinDate.getMonth() == 12 && MinDate.getHours() >= 12) || MinDate.getHours() >= 14) {
    MinDate.setDate(MinDate.getDate() + 1);
}
const MaxDate = new Date();
MaxDate.setMonth(MaxDate.getMonth() + 3);

export type EasePickDivElement = HTMLInputElement & {
    lazy?: boolean;
    durationMode?: BookingDuration;
    pickerInstance?: easepick.Core;
};

export function BookingCalendar() {
    const {appConfig, localConfig} = useContext(OscarContext) as OscarContextType

    const {setValue, resetField, watch} = useFormContext<z.infer<typeof BookingFormSchema>>();
    const duration = watch("duration");
    const calendarRef = useRef<EasePickDivElement>(null);

    /**
     * Update the date range based on the duration mode.
     *
     * @param picker
     * @param start
     * @param disabledDates
     * @param durationMode
     */
    const updateDateRange = (picker: Core, start: DateTime, disabledDates: Set<string>, durationMode: BookingDuration) => {
        // Set the range.
        const baseDays = BookingDurationHelper.getTotalDays(durationMode) ?? 0;
        const startDate = getValidDate(disabledDates, start);
        if (baseDays == 0) {
            picker.setStartDate(startDate)
            fillForm(startDate, null);
            return;
        }
        const targetDate = new DateTime(startDate);
        // The base days are inclusive, so we need to subtract 1.
        targetDate.setDate(targetDate.getDate() + (baseDays - 1));
        const newDate = getValidDate(disabledDates, targetDate);
        picker.setDateRange(startDate, newDate);
        picker.gotoDate(startDate);
        fillForm(startDate, newDate);
        return;
    }

    /**
     * Update the form values.
     *
     * @param start
     * @param end
     */
    const fillForm = (start: DateTime | null, end: DateTime | null) => {
        if (!start || !end) {
            resetField("startDate");
            resetField("endDate");
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            setValue("startDate", undefined);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            setValue("endDate", undefined);
            return;
        }

        setValue("startDate", start.toJSDate());
        setValue("endDate", end.toJSDate());
    }

    useEffect(() => {
        const element = calendarRef.current;
        if (!element) {
            return;
        }

        // Check if we need to lazy load the calendar
        const shouldUseLazyMode = duration != BookingDuration.CUSTOM;

        if (element.pickerInstance != null) {
            if (element.lazy == shouldUseLazyMode) {
                // No need to reinitialize the calendar

                if (element.durationMode != duration) {
                    if (element.pickerInstance.getStartDate() != null) {
                        // Update the date range
                        updateDateRange(element.pickerInstance, element.pickerInstance.getStartDate(), new Set<string>(appConfig?.configData?.disabledDates ?? []), duration);
                    } else {
                        fillForm(null, null);
                    }

                    // Update the duration mode
                    element.durationMode = duration;
                }

                return;
            }

            // We need to destroy the calendar
            element.pickerInstance.clear();
            element.pickerInstance.destroy();
        }

        // Update the duration mode
        element.durationMode = duration;

        // Initialize the calendar
        const calendarCount = Math.min(Math.max(localConfig?.calendarCount ?? 2, 1), 3);
        const disabledDates = new Set<string>(appConfig?.configData?.disabledDates ?? []);
        element.lazy = shouldUseLazyMode;
        element.pickerInstance = new easepick.create({
            element: calendarRef.current,
            css: [coreStyle, rangeStyle, lockStyle, customRangeStyle, customDropOffStyle],
            zIndex: 10,
            format: "YYYY-MM-DD",
            inline: true,
            calendars: calendarCount,
            readonly: false,
            grid: calendarCount,
            CustomRangePlugin: {
                lazy: shouldUseLazyMode,
                tooltip: false
            },
            LockPlugin: {
                minDate: MinDate,
                maxDate: MaxDate,
                filter(date) {
                    if (date instanceof DateTime) {
                        // Disable Saturday and Sunday
                        if (date.toJSDate().getDay() == 0 || date.toJSDate().getDay() == 6) {
                            return true;
                        } else if (disabledDates.has(date.format("YYYY-MM-DD"))) {
                            return true;
                        }
                    }

                    return false;
                },
            },
            plugins: [
                CustomRangePlugin,
                LockPlugin,
            ],
            setup(picker: Core) {
                picker.on("select", (e: CustomEvent) => {
                    const detail = e.detail;
                    if (element.lazy) {
                        updateDateRange(picker, detail.start, disabledDates, element.durationMode ?? BookingDuration.CUSTOM);
                        return;
                    }

                    // Send the dates to the server
                    fillForm(detail.start, detail.end);
                });
            }
        })
        element.pickerInstance.gotoDate(MinDate);

        fillForm(null, null);

    }, [appConfig?.configData?.disabledDates, duration, fillForm, localConfig?.calendarCount, updateDateRange]);

    return (
        <div className={"md:os-col-span-2 os-text-center os-mt-2"}>
            <div className={"os-mb-2"}>
                { duration == BookingDuration.CUSTOM ? "Choose the drop off and pickup dates" : "Choose your drop off date" }
            </div>
            <input type={"hidden"} ref={calendarRef}></input>
        </div>
    );
}

/**
 * Get the inputs "valid" date, this will check if the date is in the past, if it's a weekend, if it's disabled, etc.
 *
 * If the date is invalid, it will return the next valid date.
 *
 * @param disabledDates
 * @param input
 * @private
 */
function getValidDate(disabledDates: Set<string>, input: DateTime|Date): DateTime {
    const date = new DateTime(input);

    // If we have a min date, check it, if not, check if it's in the past.
    if (MinDate != null) {
        if (date < MinDate) {
            return new DateTime(MinDate);
        }
    } else {
        if (date < new Date()) {
            return new DateTime();
        }
    }

    // Do a max date check.
    if (MaxDate != null) {
        if (date > MaxDate) {
            return new DateTime(MaxDate);
        }
    }

    // Is it a weekend?
    if (date.getDay() == 0 || date.getDay() == 6) {
        // Move it forward a day then do the check again.
        const newDate = new DateTime(date);
        newDate.setDate(date.getDate() + 1);
        return getValidDate(disabledDates, newDate);
    }

    // Is it a disabled date?
    if (disabledDates.has(date.format("YYYY-MM-DD"))) {
        // Move it forward a day then do the check again.
        const newDate = new DateTime(date);
        newDate.setDate(date.getDate() + 1);
        return getValidDate(disabledDates, newDate);
    }

    return date;
}